import React, { useEffect, useMemo, useRef, useState } from 'react';
import { styles } from '../../config/styles';
import { formatCurrency, formatTasaForDisplay, formatNumberForDisplay } from '../../utils/formatters';
import { groupOrdenesPorTasa } from '../../utils/licitacionesCalculator';
import StackedTable from '../common/StackedTable';

const toggleStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.4rem',
  padding: 0,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontFamily: "'Raleway', sans-serif",
  fontSize: '0.8rem',
  fontWeight: 600,
  color: styles.colors.primary,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const thResumenStyle = {
  padding: '0.35rem 0.75rem',
  textAlign: 'right',
  fontSize: '0.68rem',
  fontWeight: 600,
  color: '#6B7281',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #E5E7EB',
};

const tdResumenStyle = {
  padding: '0.35rem 0.75rem',
  textAlign: 'right',
  fontSize: '0.8rem',
  color: '#374151',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #F3F4F6',
};

// `indeterminate` no existe como atributo HTML: solo se setea por la propiedad del DOM.
const CheckboxTramo = ({ checked, indeterminate, ...props }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return <input ref={ref} type="checkbox" checked={checked} {...props} />;
};

/**
 * Resumen de la demanda de una especie agrupada por valor ofertado: cuánto VN se
 * pidió a cada tasa (o a cada precio) y cuántos clientes hay detrás. Es la vista
 * que se necesita para cargar el libro en el mercado, donde lo que importa es el
 * agregado por tasa y no cada cliente por separado.
 *
 * Se renderiza como una fila más del cuerpo de la especie dentro de la tabla de
 * órdenes, así que respeta el filtro por comercial que se esté aplicando arriba.
 *
 * Con `canMarcarCargado`, cada tramo tiene un checkbox para marcar todas sus
 * órdenes como cargadas (o desmarcarlas si ya lo estaban todas). La escritura y
 * la confirmación quedan del lado de `onMarcarCargado(grupo, cargado)`.
 */
const ResumenPorTasa = ({ especie, ordenes, colSpan, canMarcarCargado = false, onMarcarCargado }) => {
  const [expandido, setExpandido] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  const grupos = useMemo(() => groupOrdenesPorTasa(ordenes, especie), [ordenes, especie]);

  if (grupos.length === 0) return null;

  const esModalidadTasa = especie?.modalidad === 'Tasa';
  const hayAdjudicado = grupos.some(g => g.vnAdjudicado > 0);
  const hayCorte = grupos.some(g => g.adjudicaAlCorte !== null);
  const monedaEmision = especie?.monedaEmision === 'USD' ? 'USD' : 'Pesos';

  const formatValor = (grupo) => {
    if (grupo.esNoCompetitivo) return 'No Competitivo';
    if (!grupo.valor) return 'Sin dato';
    return esModalidadTasa ? formatTasaForDisplay(grupo.valor) : formatNumberForDisplay(grupo.valor);
  };

  // Mismo criterio de color que las filas de órdenes: verde adjudica, rojo no.
  const colorFila = (grupo) => {
    if (grupo.adjudicaAlCorte === null) return 'transparent';
    return grupo.adjudicaAlCorte ? styles.colors.green : styles.colors.red;
  };

  // Tramo completo -> se desmarca entero; vacío o a medias -> se completan las pendientes.
  const handleToggleCargado = async (grupo) => {
    if (!canMarcarCargado || !onMarcarCargado || savingKey) return;
    setSavingKey(grupo.key);
    try {
      await onMarcarCargado(grupo, !grupo.todasCargadas);
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <tr>
      <td style={{ ...styles.td, textAlign: 'left', padding: '0.6rem 1rem', backgroundColor: styles.colors.lightGray }} colSpan={colSpan}>
        <button
          type="button"
          onClick={() => setExpandido(prev => !prev)}
          aria-expanded={expandido}
          style={toggleStyle}
          title="Demanda agrupada por valor ofertado"
        >
          <span style={{ fontSize: '0.65rem' }}>{expandido ? '▾' : '▸'}</span>
          {esModalidadTasa ? 'Órdenes por tasa' : 'Órdenes por precio'}
          <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#6B7281' }}>
            · {grupos.length} {grupos.length === 1 ? 'tramo' : 'tramos'}
          </span>
        </button>

        {expandido && (
          <StackedTable style={{ borderCollapse: 'collapse', marginTop: '0.5rem', minWidth: '520px' }}>
            <thead>
              <tr>
                <th style={{ ...thResumenStyle, textAlign: 'left' }}>
                  {esModalidadTasa ? 'Tasa ofertada' : 'Precio ofertado'}
                </th>
                <th style={thResumenStyle}>VN Solicitado</th>
                <th style={thResumenStyle}>% Demanda</th>
                <th style={thResumenStyle}>Clientes</th>
                {!esModalidadTasa && <th style={thResumenStyle}>$ Estimado</th>}
                {hayAdjudicado && <th style={thResumenStyle}>VN Adjudicado</th>}
                <th style={thResumenStyle}>Cargadas</th>
              </tr>
            </thead>
            <tbody>
              {grupos.map(grupo => (
                <tr key={grupo.key} style={{ backgroundColor: colorFila(grupo) }}>
                  <td style={{ ...tdResumenStyle, textAlign: 'left', fontWeight: 600 }}>
                    {formatValor(grupo)}
                    {grupo.adjudicaAlCorte === false && (
                      <span style={{ marginLeft: '0.5rem', fontWeight: 400, fontSize: '0.7rem', color: styles.colors.redText }}>
                        no adjudica
                      </span>
                    )}
                  </td>
                  <td style={{ ...tdResumenStyle, fontWeight: 600 }}>{formatNumberForDisplay(grupo.vnSolicitado, 0)}</td>
                  <td style={tdResumenStyle}>{formatNumberForDisplay(grupo.pctDelTotal * 100, 1)}%</td>
                  <td style={tdResumenStyle}>
                    {grupo.cantidadClientes}
                    {/* Un mismo cliente puede haber ofertado dos veces a la misma tasa. */}
                    {grupo.cantidadOrdenes !== grupo.cantidadClientes && (
                      <span style={{ color: '#6B7281' }}> ({grupo.cantidadOrdenes} órdenes)</span>
                    )}
                  </td>
                  {!esModalidadTasa && (
                    <td style={tdResumenStyle}>{formatCurrency(grupo.montoEstimado, monedaEmision)}</td>
                  )}
                  {hayAdjudicado && (
                    <td style={tdResumenStyle}>
                      {grupo.vnAdjudicado > 0 ? formatNumberForDisplay(grupo.vnAdjudicado, 0) : '-'}
                    </td>
                  )}
                  <td style={tdResumenStyle}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                      {canMarcarCargado && (
                        <CheckboxTramo
                          checked={grupo.todasCargadas}
                          indeterminate={grupo.cantidadCargadas > 0 && !grupo.todasCargadas}
                          onChange={() => handleToggleCargado(grupo)}
                          disabled={savingKey !== null}
                          style={{ cursor: savingKey !== null ? 'wait' : 'pointer', width: '15px', height: '15px', margin: 0 }}
                          aria-label={`Marcar como cargadas las órdenes de ${formatValor(grupo)}`}
                          title={grupo.todasCargadas
                            ? 'Desmarcar todas las órdenes de este tramo'
                            : 'Marcar todas las órdenes de este tramo como cargadas en el mercado'}
                        />
                      )}
                      {grupo.cantidadCargadas} / {grupo.cantidadOrdenes}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </StackedTable>
        )}

        {expandido && hayCorte && (
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.7rem', color: '#6B7281' }}>
            El color marca qué tramos quedan dentro del corte informado. Lo efectivamente adjudicado
            puede ser menor por prorrateo del colocador.
          </p>
        )}
      </td>
    </tr>
  );
};

export default ResumenPorTasa;
